<template>
  <div class="login-form-container">
    <div class="logo">
      <h4>{{ $t('Welcome to') }} UPDF</h4>
    </div>
    <el-form ref="refForm" :model="formData" :rules="formRules" class="form-container" onsubmit="return false" @keyup.enter="submitForm()">
      <el-form-item prop="username" class="form-item">
        <AutoInput v-model="formData.username" :disabled="disabledAccountInput" name="username" class="form-item-input" type="account" :placeholder="inputPlaceholder" />
      </el-form-item>
      <el-button class="submit-btn" size="large" type="primary" :disabled="formData.username.length === 0" @click="submitForm()" :loading="btnLoading">{{ $t('Continue') }}</el-button>
      <p class="register-tip">
        {{ $t(appStore.isCN ? 'Unregistered account will be registered after entering the verification code' : 'Unregistered email will be registered after entering the verification code') }}
      </p>
    </el-form>
    <template v-if="!isJoinCompany">
      <div class="other-login-type">
        <GoogleLogin v-if="!appStore.isCN"></GoogleLogin>
        <AppleLogin></AppleLogin>
        <button v-if="appStore.isCN" @click="routerTo('loginWechat', query)" class="third-party-login">
          <img class="icon" src="@/assets/icon-wechat.svg" v-svg-inline />
          {{ $t('Log in with WeChat Scan') }}
        </button>
        <a class="other-login-btn" @click="routerTo('loginAccountSso', query )" v-html="$t('You can also <span>continue with SSO</span>')"></a>
      </div>
    </template>
    <Privacy />
  </div>
</template>

<script setup>
import { checkUsernameExists } from '@/api/user/user'
import AutoInput from '@/components/auto-input.vue'
import config from '@/config'
import gtagTools from '@/libs/gtag-tools'
import { getOSName, inputAutofocus, testValue } from '@/libs/tools'
import { getSessionStorage, postMessageToOpenerWindow, redirectApp, routerTo, setLocalStorage, setSessionStorage } from '@/libs/utils'
import { setReferrer } from '@/services/base'
import { setLoginAccountDataToSession } from '@/services/login'
import documentReady from 'document-ready'
import { ElMessage } from 'element-plus'
import { computed, getCurrentInstance, onMounted, reactive, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import AppleLogin from '../components/apple-login.vue'
import GoogleLogin from '../components/google-login.vue'
import Privacy from '../components/privacy.vue'
import { appStore, userStore } from '@/stores'

const $router = useRouter()
const { query } = useRoute()

// 是否是加入企业流程，加入企业流程不允许第三方登录
const isJoinCompany = query?.action === 'joinCompany'
const inviteCode = isJoinCompany ? userStore.inviteCode : ''
const account = query?.account || appStore.defaultAccount
const disabledAccountInput = appStore.disabledAccountInput
const password = query?.password || ''

// 打开客户端(如果有账号和密码说明已经注册过，则自动尝试打开移动端并携带账号和密码)
const openUpdfLogin = () => {
  const osName = getOSName()
  if ((osName === 'ANDROID' || osName === 'IOS') && password) {
    redirectApp('login', {
      account,
      inviteCode,
      password,
    })
  }
}

const { $t } = getCurrentInstance().proxy

const loginFormData = userStore.loginFormData
loginFormData.isRegister = false

// 如果是邀请加入则只能输入邮箱或手机（海外只能是邮箱）
let inputPlaceholder = isJoinCompany ? $t('Email/phone number') : $t('Account/email/phone number')
if (!appStore.isCN) inputPlaceholder = isJoinCompany ? $t('Email') : $t('Account/email')
let inputErrorMsg = isJoinCompany ? $t('Please enter an effective email/phone number') : $t('Please enter an effective account/email/phone number')
if (!appStore.isCN) inputErrorMsg = isJoinCompany ? $t('Please enter an effective email') : $t('Please enter an effective account/email')

const formData = reactive({
  username: account || loginFormData.username,
})
document.title = $t('UPDF Login')
// 如果是直接访问不是从入口文件跳转过来的，设置设备类型为WEB，以便登录后直接进入用户中心
const fromIndex = getSessionStorage('fromIndex')
if (!fromIndex) {
  const deviceType = 'WEB'
  setLocalStorage('deviceType', deviceType)
  setLocalStorage('source', deviceType)
  appStore.deviceType = deviceType
  appStore.source = deviceType
}
// 判断账号是否有修改，有修改则清除验证码缓存
const oldUsername = loginFormData.username

const formError = ref(false) // 防止有错误时点击提交错误信息会闪两次
const refForm = ref()
const btnLoading = ref(false)
const isCN = ref(appStore.isCN)

setReferrer()


const formRules = reactive({
  username: [
    { required: true, message: inputErrorMsg, trigger: 'change' },
    {
      validator: (rule, value, callback) => {
        // 中文允许账号手机号和邮箱，英文只允许账号邮箱
        let testAccount = false
        if (isJoinCompany) {
          if (isCN.value) {
            testAccount = testValue('phone', value) || testValue('email', value)
          } else {
            testAccount = testValue('email', value)
          }
        } else if (isCN.value) {
          testAccount = testValue('phone', value) || testValue('email', value) || testValue('account', value)
        } else {
          testAccount = testValue('email', value) || testValue('account', value)
        }
        if (value === '' || testAccount) {
          formError.value = false
          callback()
        } else {
          formError.value = true
          callback(new Error(inputErrorMsg))
        }
      },
      trigger: 'blur',
    },
  ],
})

// 输入的账号类型
const accountType = computed(() => {
  if (testValue('email', formData.username)) {
    return 'email'
  } else if (testValue('phone', formData.username)) {
    return 'phone'
  } else {
    return 'account'
  }
})

// 输入的是否是账号
const isAccount = computed(() => accountType.value === 'account')

const submitForm = () => {
  // 输入框中直接按enter时，需要先input取消光标，验证数据，如果验证失败，在把光标放进去
  const activeElement = document.activeElement
  activeElement.blur()
  setTimeout(() => {
    if (!refForm.value || formError.value) {
      activeElement.focus()
      return
    }
    refForm.value.validate(async valid => {
      if (valid && !btnLoading.value) {
        btnLoading.value = true
        loginFormData.username = formData.username
        // 账号类型: email/phone/account
        loginFormData.accountType = accountType.value

        setLoginAccountDataToSession(loginFormData.username, loginFormData.accountType)
        if (oldUsername !== formData.username) {
          setSessionStorage('loginCodeLastTime', '')
          setSessionStorage('forgetPassCodeLastTime', '')
        }
        // 防止网络较慢时下个页面没有加载出来时候一直停留在该页面
        try {
          userStore.loginFormData = loginFormData
          gtagTools.accountInput(loginFormData.accountType)
          const requestParams = { username: formData.username, inviteCode }
          await checkUsernameExists(requestParams)
          btnLoading.value = false
          // 账号密码都存在：跳转到优先密码登录
          userStore.accountIsExist = true
          userStore.passwordIsExist = true
          $router.push({ name: 'loginAccountPass', query })
        } catch (data) {
          if (data.code === 2410) {
            // 账号存在，密码不存在：跳转到验证码登录，并隐藏密码登录
            userStore.accountIsExist = true
            userStore.passwordIsExist = false
            $router.push({ name: 'loginAccountCode', query })
          } else if (data.code === 2413) {
            // 账号存在，且其中至少一个账号有密码：跳转到验证码登录，并显示密码登录
            userStore.accountIsExist = true
            userStore.passwordIsExist = true
            $router.push({ name: 'loginAccountCode', query })
          } else if (isAccount.value) {
            // 账号不存在时，提示错误
            const errMsg = isCN.value ? $t('Please enter an effective account/email/phone number') : $t('Please enter an effective account/email')
            ElMessage.error(errMsg)
            inputAutofocus('username')
          } else {
            userStore.accountIsExist = false
            userStore.passwordIsExist = false
            // 账号密码都不存在：跳转到验证码登录，并显示稍后验证入口
            $router.push({ name: 'loginAccountCode', query })
          }
          btnLoading.value = false
        }
      } else {
        return false
      }
    })
  }, 100)
  return false
}

onMounted(() => {
  documentReady(() => {
    postMessageToOpenerWindow(config.iframeReadyActionName)
    inputAutofocus('username')
  })
  openUpdfLogin()
})
</script>

<style lang="scss" scoped>
.is-join-company {
  .form-container {
    margin-top: 100px;
  }
}
.form-container {
  margin-top: 24px;
  .form-item {
    margin-bottom: 35px !important;
  }
  .register-tip {
    text-align: center;
    font-size: 12px;
    color: var(--BG-5disable-text, #AAAAAA);
    line-height: 16px;
  }
}
</style>
